import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import ClientNav from '@/components/ClientNav';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminRequests = () => {
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/admin/requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data.requests);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    }
  };

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      await axios.put(
        `${API}/admin/requests/${requestId}?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Request status updated');
      fetchRequests();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500/10 text-green-400';
      case 'in_progress': return 'bg-blue-500/10 text-blue-400';
      default: return 'bg-yellow-500/10 text-yellow-400';
    }
  };

  return (
    <div className="min-h-screen bg-[#030712]" data-testid="admin-requests-page">
      <ClientNav />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold font-['Syne'] text-white mb-2" data-testid="requests-title">Requests</h1>
          <p className="text-slate-400">Manage all client requests</p>
        </div>

        {requests.length === 0 ? (
          <div className="bg-slate-950 border border-slate-800 rounded-xl p-12 text-center" data-testid="no-requests">
            <FileText className="w-16 h-16 text-slate-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No requests yet</h3>
            <p className="text-slate-400">Client requests will appear here</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="requests-list">
            {requests.map((request) => (
              <div
                key={request.id}
                className="bg-slate-950 border border-slate-800 rounded-xl p-6"
                data-testid={`request-${request.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-xl font-semibold text-white">{request.title}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                        {request.status.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="text-sm text-slate-400 mb-2">From: {request.client_email}</p>
                    <p className="text-slate-300">{request.description}</p>
                  </div>
                  <div className="ml-4">
                    <Select
                      value={request.status}
                      onValueChange={(value) => handleStatusChange(request.id, value)}
                    >
                      <SelectTrigger className="bg-slate-900 border-slate-800 w-40" data-testid={`status-select-${request.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800 text-white">
                        <SelectItem value="submitted">Submitted</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="text-sm text-slate-500">
                  Created: {format(new Date(request.created_at), 'MMM dd, yyyy h:mm a')}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminRequests;
